import React from 'react';
import moment from 'moment-timezone';
import { flatMap } from 'lodash';
import { CoachAvailabilityInterval, CoachAvailabilityInput, DayOfWeek } from 'globalTypes';
import { GetCoachAvailabilities_coaching_coachAvailability_availabilityExceptions } from 'coaching/queries/__generated__/GetCoachAvailabilities';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import CoachingAvailabilityDayRow from './CoachingAvailabilityDayRow';
import CoachingAvailabilityMultiDayRow from './CoachingAvailabilityMultiDayRow';
import Container from 'components/Container';
import Divider from 'components/Divider';
import ErrorReporter from 'app/components/ErrorReporter';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Label from 'components/Label';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import Row from 'components/Row';
import EventTracking from 'lib/EventTracking';

import {
  getWeeklyTimeframeString,
  calculateTotalHoursFromIntervals,
  getDayOfWeekIndex,
} from 'coaching/lib/timeIntervals';

type Props = {
  errorMessage?: string;
  loadingSchedule: boolean;
  weekOffset: number;
  existingAvailabilities?: CoachAvailabilityInput[];
  existingAvailabilityExceptions?: (GetCoachAvailabilities_coaching_coachAvailability_availabilityExceptions | null)[];
  onClickLeftChevron: () => void;
  onClickRightChevron: () => void;
  onClickEditLink: () => void;
  onClickAddTimeOff: () => void;
  deleteCoachingAvailabilityException: (exceptionUuid: string) => void;
};

function renderAvailability(availability: CoachAvailabilityInput, weekOffset: number) {
  if (!availability || !availability.timeSlot) {
    return;
  }

  const intervals = availability.timeSlot.intervals.map(interval => ({
    startTime: interval?.startTime,
    endTime: interval?.endTime,
  }));

  const row = (
    <CoachingAvailabilityDayRow
      dayOfWeek={availability.timeSlot.dayOfWeek}
      dayOfWeekIndex={getDayOfWeekIndex(availability.timeSlot.dayOfWeek)}
      weekOffset={weekOffset}
      intervals={intervals}
    />
  );

  return (
    <Row size="xsmall" key={availability.timeSlot.dayOfWeek}>
      {row}
    </Row>
  );
}

function renderAvailabilityExceptions(
  availabilityExceptions: GetCoachAvailabilities_coaching_coachAvailability_availabilityExceptions[],
  weekOffset: number,
  deleteCoachingAvailabilityException: (exceptionUuid: string) => void
) {
  if (availabilityExceptions.length === 0) {
    return null;
  }

  return (
    <Container size="xsmall" className="CoachingIsolatedView-timeOffSection">
      <Row size="small">
        <Label>Unavailable</Label>
      </Row>
      {availabilityExceptions.map((exception, index) => {
        const startDateTimeMoment = moment(exception.startDateTime);
        const endDateTimeMoment = moment(exception.endDateTime);

        const startDayOfWeek = startDateTimeMoment.format('dddd') as DayOfWeek;
        const interval: CoachAvailabilityInterval = {
          startTime: startDateTimeMoment.format('HH:mm'),
          endTime: endDateTimeMoment.format('HH:mm'),
        };
        const key = `${startDayOfWeek}-${index}`;
        const isSameDayOfYear = startDateTimeMoment.dayOfYear() === endDateTimeMoment.dayOfYear();
        const isSameYear = startDateTimeMoment.year() === endDateTimeMoment.year();
        const isSingleDayException = isSameDayOfYear && isSameYear;

        const row = isSingleDayException ? (
          <Text color="thunder" key={key}>
            <CoachingAvailabilityDayRow
              key={startDayOfWeek}
              dayOfWeek={startDayOfWeek}
              dayOfWeekIndex={getDayOfWeekIndex(startDayOfWeek)}
              weekOffset={weekOffset}
              intervals={[interval]}
              onRemove={() => {
                deleteCoachingAvailabilityException(exception.uuid);
                EventTracking.trackKey('coaching.view_coach_availability.remove_time_off.click');
              }}
            />
          </Text>
        ) : (
          <Text color="thunder" key={key}>
            <CoachingAvailabilityMultiDayRow
              startDateTime={exception.startDateTime}
              endDateTime={exception.endDateTime}
              onRemove={() => {
                deleteCoachingAvailabilityException(exception.uuid);
                EventTracking.trackKey('coaching.view_coach_availability.remove_time_off.click');
              }}
            />
          </Text>
        );

        return availabilityExceptions.length > index + 1 ? (
          <Row size="xsmall" key={key}>
            {row}
          </Row>
        ) : (
          row
        );
      })}
    </Container>
  );
}

export default function CoachingAvailabilityReadView(props: Props) {
  const {
    existingAvailabilities,
    weekOffset,
    errorMessage,
    loadingSchedule,
    existingAvailabilityExceptions,
  } = props;

  React.useEffect(() => {
    EventTracking.trackKey('coaching.view_coach_availability.view');
  }, []);

  if (errorMessage) {
    return <ErrorReporter error={errorMessage} />;
  }

  const nonEmptyAvailabilities =
    existingAvailabilities?.filter(
      availability =>
        availability?.timeSlot?.intervals && availability?.timeSlot?.intervals.length > 0
    ) ?? [];
  const allIntervals = flatMap(
    nonEmptyAvailabilities,
    availability => availability?.timeSlot?.intervals
  );
  const nonEmptyAvailabilityExceptions = existingAvailabilityExceptions?.filter(
    (
      exception
    ): exception is GetCoachAvailabilities_coaching_coachAvailability_availabilityExceptions =>
      !!exception
  );

  const renderSchedule = () => {
    return (
      <React.Fragment>
        <Container size="xsmall">
          <Row size="small">
            <Label>Availability ({calculateTotalHoursFromIntervals(allIntervals)} hrs)</Label>
          </Row>
          {nonEmptyAvailabilities.map(availability => renderAvailability(availability, weekOffset))}
        </Container>
        {nonEmptyAvailabilityExceptions &&
          renderAvailabilityExceptions(
            nonEmptyAvailabilityExceptions,
            weekOffset,
            props.deleteCoachingAvailabilityException
          )}
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <Container size="xsmall">
        <Row size="small">
          <MediaObject center figure="right">
            <MediaObject center>
              <Link
                onClick={props.onClickLeftChevron}
                eventKey="coaching.view_coach_availability.select_week_arrow.click">
                <Icon name="chevron-left" color="pitch" />
              </Link>
              <Text align="center">
                <Heading serif>{getWeeklyTimeframeString(weekOffset)}</Heading>
              </Text>
            </MediaObject>
            <Link
              onClick={props.onClickRightChevron}
              eventKey="coaching.view_coach_availability.select_week_arrow.click">
              <Icon name="chevron-right" color="pitch" />
            </Link>
          </MediaObject>
        </Row>
      </Container>
      <Row size="small">{loadingSchedule ? <CenteredLoadingIndicator /> : renderSchedule()}</Row>
      <Container size="xsmall">
        <Row size="xsmall">
          <Link
            onClick={props.onClickAddTimeOff}
            eventKey="coaching.view_coach_availability.add_time_off.click">
            <TextWithIcon icon="+" weight="semibold" size="small">
              Add time off
            </TextWithIcon>
          </Link>
        </Row>
        <Divider size="medium" />
        <Text>
          To update your normal hours, you can{' '}
          <Link
            onClick={props.onClickEditLink}
            eventKey="coaching.view_coach_availability.change_availability.click">
            change availability
          </Link>
          .
        </Text>
      </Container>
    </React.Fragment>
  );
}
